import FormContainer, { SubmitButton } from "components/form";
import { ButtonLevel } from "components/ui/Button";
import DollarStackAnimation from "components/ui/DollarStackAnimation";
import InfoList, { InfoListItem } from "components/ui/InfoList";
import UILabel from "components/ui/UILabel";
import { useEffect, useState } from "react";
import { getDashboard, getUsersProfile } from "store/api/apiSlice";
import styled from "styled-components";
import {
  ApiUsersPayrollAccountCompensationUnit,
  ApiUsersPayrollAccountPayFrequency,
} from "utils/api/users";
import { formatDateString } from "utils/date";
import { getPartnerConfig } from "utils/partner";
import Layout from "./Layout";

const PendingWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  flex: 1 1 auto;
`;

const PAY_FREQUENCY_MAP = {
  [ApiUsersPayrollAccountPayFrequency.weekly]: "Weekly",
  [ApiUsersPayrollAccountPayFrequency.biweekly]: "Biweekly",
  [ApiUsersPayrollAccountPayFrequency.semimonthly]: "Semimonthly",
  [ApiUsersPayrollAccountPayFrequency.monthly]: "Monthly",
};

const MESSAGES = [
  "Detecting income streams",
  "Verifying employment",
  "Confirming direct deposit history",
];

const Review = ({ goToNextStep }) => {
  const { data: profile } = getUsersProfile();
  const { data: dashboard } = getDashboard();
  const partner = getPartnerConfig();
  const [isPending, setIsPending] = useState(true);
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPending(false);
    }, 6_000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prevIndex) => (prevIndex + 1) % MESSAGES.length);
    }, 2_000);

    return () => clearInterval(interval);
  }, []);

  const onSubmit = async () => {
    await goToNextStep();
  };

  const message = MESSAGES[messageIndex];

  if (isPending) {
    return (
      <PendingWrapper>
        <DollarStackAnimation />
        <UILabel.Large>{message}...</UILabel.Large>
      </PendingWrapper>
    );
  }

  // TODO: Replace with real data
  const { payrollAccount } = profile || {};
  const employerName = payrollAccount?.employerName || "Employer Name";
  const directDepositInstitutionName = partner.name || "Financial Institution";
  const incomeType =
    payrollAccount?.compensationUnit ===
    ApiUsersPayrollAccountCompensationUnit.hourly
      ? "Hourly"
      : "Salary";
  const payFrequency =
    PAY_FREQUENCY_MAP[payrollAccount?.payFrequency] || "Unknown";
  const nextPayday = dashboard?.nextPayday;

  const items: InfoListItem[] = [
    {
      icon: "briefcase",
      label: "Employer",
      value: employerName,
    },
    {
      icon: "income",
      label: "Income type",
      value: incomeType,
    },
    {
      icon: "bank",
      label: "Direct deposit",
      value: directDepositInstitutionName,
    },
    {
      icon: "calendar",
      label: "Frequency",
      value: payFrequency,
    },
    {
      icon: "party",
      label: "Next payday",
      value: formatDateString(nextPayday, "textShortWithWeekday"),
    },
  ];

  return (
    <FormContainer onSubmit={onSubmit}>
      <Layout
        heading="We found your payroll!"
        subheading="You can start accessing a portion of your accrued net earnings instantly."
        actions={
          <SubmitButton level={ButtonLevel.cta} fullWidth>
            Continue
          </SubmitButton>
        }
      >
        <InfoList items={items} />
      </Layout>
    </FormContainer>
  );
};

export default Review;
